import { useEffect, useState } from "react";
import { apiClient } from "@/context/AuthContext";
import { AlertTriangle, CheckCircle2, Clock, Download, FileText, Loader2, Mail, Package, Send } from "lucide-react";

const VERDICT_ICON = { pass: CheckCircle2, fail: AlertTriangle, pending: Clock };

const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

function SendForm({ assessmentId, defaultTo, onToast }) {
  const [to, setTo] = useState(defaultTo || "");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/assessments/${assessmentId}/results/email`, { to, message });
      onToast(`Sonuç e-postası gönderildi: ${to} (${data.attachments.length} ek)`);
      setMessage("");
    } catch (err) { onToast(err?.response?.data?.detail || "E-posta gönderilemedi"); }
    finally { setBusy(false); }
  };
  return (
    <form className="results-send" onSubmit={send} data-testid="results-email-form">
      <div className="eyebrow"><Mail size={11}/> BİNA SAHİBİNE GÖNDER</div>
      <input type="email" required value={to} onChange={(e) => setTo(e.target.value)} placeholder="ornek@firma.com" data-testid="results-email-to"/>
      <textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Kısa not (isteğe bağlı)" data-testid="results-email-message"/>
      <button className="primary-btn" disabled={busy} data-testid="results-email-send">
        {busy ? <Loader2 size={14} className="spin"/> : <Send size={14}/>} Sonuç özeti + teknik raporu gönder
      </button>
    </form>
  );
}

export default function ResultsPanel({ assessmentId, latestReport, onDownloadReport, onToast, version }) {
  const [res, setRes] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => { (async () => {
    try { const { data } = await apiClient.get(`/assessments/${assessmentId}/results`); setRes(data); } catch { /* ignore */ }
  })(); }, [assessmentId, version]);

  const downloadSummary = async () => {
    setPdfBusy(true);
    try {
      const r = await apiClient.get(`/assessments/${assessmentId}/results/pdf`, { responseType: "blob" });
      saveBlob(r.data, `sonuc-ozeti-${assessmentId}.pdf`);
    } catch { onToast("Sonuç özeti indirilemedi"); } finally { setPdfBusy(false); }
  };

  if (!res) return <div className="section-block results-block"><Loader2 size={16} className="spin"/></div>;
  const v = res.verdict;
  const Icon = VERDICT_ICON[v.level];

  return (
    <section className="section-block results-block" data-testid="results-panel">
      <div className="section-title"><div><div className="eyebrow">SONUÇ</div><h2>Sonuç ve yapılacaklar</h2></div></div>
      <div className={`verdict-banner ${v.level}`} data-testid="results-verdict">
        <Icon size={28}/>
        <div><strong>{v.title}</strong><p>{v.sentence}</p></div>
      </div>
      <div className="results-grid">
        <div className="results-todo" data-testid="results-todo-list">
          <h3>Yapılması gerekenler <span>{res.todo.length}</span></h3>
          {res.todo.map((t, i) => (
            <article key={t.finding_id} className={`todo-row sev-${t.severity}`} style={{ animationDelay: `${Math.min(i, 10) * 40}ms` }} data-testid={`results-todo-${t.finding_id}`}>
              <b>{i + 1}</b>
              <div><strong>{t.title}</strong><p>{t.action_text}</p><small>{t.source} · Öncelik: {t.severity_label}</small></div>
              {t.quantity ? <span className="todo-qty">{t.quantity} {t.unit}</span> : null}
            </article>
          ))}
          {!res.todo.length && res.pending.length > 0 && <small className="evidence-empty">Önce bekleyen {res.pending.length} maddeyi doğrulayın; ardından yapılacaklar listesi kesinleşir.</small>}
          {!res.todo.length && !res.pending.length && <small className="evidence-empty">Giderilmesi gereken madde yok.</small>}
        </div>
        <div className="results-side">
          <div className="results-materials" data-testid="results-materials">
            <h3><Package size={14}/> Malzeme özeti</h3>
            {res.materials.map((m) => <div key={m.title}><span>{m.title}</span><b>{m.quantity} {m.unit}</b></div>)}
            {!res.materials.length && <small className="evidence-empty">Sayılabilir malzeme ihtiyacı yok.</small>}
          </div>
          <div className="results-actions">
            <button className="secondary-btn" onClick={downloadSummary} disabled={pdfBusy} data-testid="results-summary-pdf">
              {pdfBusy ? <Loader2 size={14} className="spin"/> : <FileText size={14}/>} Sonuç özeti PDF (sade dil)
            </button>
            <button className="secondary-btn" onClick={() => onDownloadReport(latestReport.id, latestReport.code)} disabled={!latestReport} data-testid="results-latest-report">
              <Download size={14}/> {latestReport ? `Son teknik raporu indir (${latestReport.code})` : "Henüz teknik rapor yok"}
            </button>
          </div>
          <SendForm assessmentId={assessmentId} defaultTo={res.owner_email} onToast={onToast}/>
        </div>
      </div>
    </section>
  );
}
